import { memo, useCallback } from "react"
import { Streamdown } from "streamdown"
import Marker from "@/components/maps/mapLibre/Marker"
import { Popup } from "@/components/maps/mapLibre/Popup"

type AgentMarkerProps = {
  longitude: number
  latitude: number
  label?: string
  color?: string
  kind?: string
  popup?: string
  /**
   * Whether this marker's popup is currently open. Controlled by the parent
   * (AgentMapLayers) so only one popup can be open at a time across the map.
   */
  isOpen?: boolean
  /** Called when the user clicks the marker — toggle open/close. */
  onToggle?: () => void
  /** Called when maplibre auto-dismisses the popup (X button or click on map). */
  onClose?: () => void
}

const PICKUP_COLORS = new Set(["#f59e0b", "orange"])

function pickEmoji(
  kind: string | undefined,
  label: string | undefined,
  color: string | undefined,
): string {
  if (kind === "fuel") return "⛽"
  if (kind === "pickup") return "📦"
  if (kind === "delivery") return "🏁"
  if (kind === "driver") return "🚛"
  if (label && PICKUP_COLORS.has(color ?? "")) return "📦"
  return "📍"
}

function pickColor(
  kind: string | undefined,
  color: string | undefined,
): string {
  if (color) return color
  if (kind === "fuel") return "#16a34a"
  if (kind === "pickup") return "#f59e0b"
  if (kind === "delivery") return "#ef4444"
  if (kind === "driver") return "#3b82f6"
  return "#3b82f6"
}

const AgentMarker = memo(
  ({
    longitude,
    latitude,
    label,
    color,
    kind,
    popup,
    isOpen,
    onToggle,
    onClose,
  }: AgentMarkerProps) => {
    const dotColor = pickColor(kind, color)
    const emoji = pickEmoji(kind, label, color)
    const clickable = !!popup && !!onToggle
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!clickable) return
        // Stop the click bubbling to the map so maplibre's `closeOnClick`
        // doesn't fire and immediately dismiss the popup we're opening.
        e.stopPropagation()
        onToggle?.()
      },
      [clickable, onToggle],
    )

    return (
      <>
        <Marker longitude={longitude} latitude={latitude}>
          <div
            className={`relative flex items-center ${clickable ? "cursor-pointer" : ""}`}
            onClick={handleClick}
          >
            <div
              className="h-3 w-3 rounded-full border-2 border-white shadow-[0_1px_4px_rgba(0,0,0,0.5)]"
              style={{ background: dotColor }}
            />
            {label && (
              <div className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-0.5 text-xs font-semibold text-popover-foreground shadow-md">
                <span className="mr-1">{emoji}</span>
                {label}
              </div>
            )}
          </div>
        </Marker>
        {popup && isOpen && (
          <Popup
            longitude={longitude}
            latitude={latitude}
            anchor="bottom"
            offset={16}
            maxWidth="260px"
            onClose={onClose}
          >
            <div
              className={[
                "agent-marker-popup",
                "prose prose-sm max-w-none text-popover-foreground",
                // Tighten Streamdown's default vertical rhythm — the stock
                // `.prose` margins double-space the small popup body.
                "prose-p:my-1 prose-strong:font-semibold",
                // Compact heading: smaller font, almost no margin.
                "prose-h1:my-0 prose-h1:mb-0.5 prose-h1:text-sm prose-h1:font-bold",
                "prose-h2:my-0 prose-h2:mb-0.5 prose-h2:text-sm prose-h2:font-bold",
                "prose-h3:my-0 prose-h3:mb-0.5 prose-h3:text-sm prose-h3:font-bold",
                // Tight list: minimal left indent, snug row gap, subtle muted
                // bullet markers (defaults are pale gray and oversized).
                "prose-ul:my-1 prose-ul:list-disc prose-ul:pl-3",
                "prose-ol:my-1 prose-ol:pl-3",
                "prose-li:my-0 prose-li:pl-0 prose-li:leading-snug",
                "marker:text-[0.65em] marker:text-muted-foreground",
                // The "Partner" / "Publiczna" source tag is the first <p> right
                // after the heading — render it as a muted uppercase chip-style
                // line so it reads as metadata, not body text.
                "[&_h1+p]:my-0 [&_h1+p]:mb-1 [&_h1+p]:text-[10px] [&_h1+p]:font-medium [&_h1+p]:uppercase [&_h1+p]:tracking-wider [&_h1+p]:text-muted-foreground",
                "[&_h2+p]:my-0 [&_h2+p]:mb-1 [&_h2+p]:text-[10px] [&_h2+p]:font-medium [&_h2+p]:uppercase [&_h2+p]:tracking-wider [&_h2+p]:text-muted-foreground",
                "[&_h3+p]:my-0 [&_h3+p]:mb-1 [&_h3+p]:text-[10px] [&_h3+p]:font-medium [&_h3+p]:uppercase [&_h3+p]:tracking-wider [&_h3+p]:text-muted-foreground",
                // Bold-only paragraph (e.g. "**Ceny:**") becomes a small caps
                // section label instead of body weight. Drop the `>` direct-
                // child combinator inside :has() — Tailwind's arbitrary-variant
                // parser handles plain descendant selectors more reliably, and
                // `<p>` only ever has the `<strong>` as a direct child here.
                "[&_p:has(strong:only-child)]:mb-0.5 [&_p:has(strong:only-child)]:mt-2 [&_p:has(strong:only-child)]:text-[10px] [&_p:has(strong:only-child)]:uppercase [&_p:has(strong:only-child)]:tracking-wider [&_p:has(strong:only-child)]:text-muted-foreground",
                "[&_p:has(strong:only-child)>strong]:font-semibold [&_p:has(strong:only-child)>strong]:text-muted-foreground",
                // Smaller font + padding so the card doesn't dominate the map
                // on dense fuel-station clusters.
                "px-3 py-2 text-xs leading-snug",
              ].join(" ")}
            >
              <Streamdown>{popup}</Streamdown>
            </div>
          </Popup>
        )}
      </>
    )
  },
)

export default AgentMarker
